import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import Formsy from 'formsy-react';
import { ChevronDown, Trash } from 'lucide-react';
import { Row, Col, Label, FormGroup, Button, Separator, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, cn, SheetFooter, SheetBody, ButtonIcon } from 'twyne-new.ui';

import InputSearchList from '@/components/InputSearchList';
import DateTimePicker from 'components/form/DateTimePicker';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import SelectAsync from 'components/form/SelectAsync';

const propTypes = {
  data: PropTypes.object,
  fields: PropTypes.object,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default class ProfileToolForm extends React.Component {
  state = {
    incommingTraffic: [],
    session: [],
    context: [],
    consumerData: [],
    customFields: [],
    metaFields: [],
    values: {},
    fieldSearch: '',
  }

  handleValidSubmit = model => {
    this.props.onSubmit(model)
  }

  getValue = key => () => this.state.values[key] || (typeof this.state.values[key].value === 'boolean') ? this.state.values[key].value : ''

  handleChange = (group, field, value) => {
    this.setState(({ values }) => {
      values[group] = { ...values[group], [field]: value }
      this.props.onChange(values)
      return { values }
    })
  }

  Field = ({ data }) => {
    const { name, type, options, loadOptions, group, mask } = data

    const value = this.state.values[group] && this.state.values[group][name]

    if (type === 'select' || type === 'boolean') {
      if (loadOptions) {
        return <SelectAsync required
          loadOptions={loadOptions}
          value={value}
          name={name}
          onChange={value => this.handleChange(group, name, value)}
        />
      }

      return <Select required
        options={options}
        value={value}
        name={name}
        setValue={value => this.handleChange(group, name, value)}
      />
    }

    if (type === 'input' || type === 'integer') {
      return <Input required
        name={name}
        type={type === 'integer' ? 'number' : 'text'}
        value={value}
        setValue={value => this.handleChange(group, name, value)}
      />
    }

    if (type === 'dateTime' || type === 'date') {
      return <DateTimePicker meridiemSystem mask={mask} required
        name={name}
        value={value}
        onChange={value => this.handleChange(group, name, value)}
        openDirection="bottom"
        isOutsideRange={() => false}
      />
    }
    return null
  }

  FieldGroup = ({ field, children }) => {
    const onRemove = field => evt => {
      evt.stopPropagation()
      const { name, group } = field
      const { [group]: stateFields, values } = this.state

      const idx = stateFields.indexOf(field)
      idx > -1 && stateFields.splice(idx, 1)
      if (values[group] && values[group][name] != undefined) {
        values[group][name] = undefined
      }

      this.setState({ [group]: stateFields, values })
    }

    return <Col size="3" className="group">
      <FormGroup>
        <div className='flex justify-between'>
          <Label>{field.label}</Label>
          <div className='twy-text-accent cursor-pointer hidden group-hover:block' onClick={onRemove(field)}>
            remove
          </div>
        </div>
        {children}
      </FormGroup>
    </Col>
  }

  Group = ({ data, children }) => {
    if (!data.length) {
      return null
    }

    return <Row>
      <Col size='12'>
        <Label className='mb-2 twy-text-base-accent'><big>{children}</big></Label>
        <Row>
          {data.map(field => <this.FieldGroup key={field.name} field={field}>
            <this.Field data={field} />
          </this.FieldGroup>)}
        </Row>
        <Separator className='m-0 my-2' />
      </Col>
    </Row>
  }

  DropdownFields = ({ data, onAdd, onRemove, isAdded }) => {
    const DropdownField = ({ field }) => <DropdownMenuItem
      className='pl-3! group'
      onClick={evt => {
        evt.stopPropagation()
        evt.preventDefault()
        onAdd(field)(evt)
      }}
    >
      <div className='flex justify-between w-full relative'>
        <span className={cn('text-sm ml-2', isAdded(field) ? 'twy-text-base-neutral' : 'twy-text-base-accent')}>{field.label}</span>
        {isAdded(field) ? <>
          <ButtonIcon content='Remove field' className='hidden group-hover:block absolute right-0 top-1/2 -translate-y-1/2'>
            <Trash size={12} onClick={onRemove(field)} />
          </ButtonIcon>
        </> : null}
      </div>
    </DropdownMenuItem>

    return data.map((field, idx) => <DropdownField key={idx} field={field} />)
  }

  matchesFieldSearch = (field, searchTerm) => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    if (!normalizedSearch) {
      return true
    }

    const label = (field.label || '').toLowerCase()
    const name = (field.name || '').toLowerCase()
    return label.includes(normalizedSearch) || name.includes(normalizedSearch)
  }

  DropdownFieldsGroup = ({ type, children, searchTerm }) => {
    const { [type]: allFields } = this.props.fields
    const filteredFields = allFields.filter(field => this.matchesFieldSearch(field, searchTerm))

    const handleChange = change => {
      const { [type]: stateFields, values } = this.state
      change(stateFields, values[type])
      this.setState({ [type]: stateFields, values })
    }

    const isAdded = field => {
      const { [type]: stateFields } = this.state
      return stateFields.includes(field)
    }

    const handleAdd = field => evt => {
      evt.stopPropagation()
      handleChange(state => {
        !state.includes(field) && state.push(field)
      })
    }

    const handleRemove = field => evt => {
      evt.stopPropagation()
      handleChange((state, valuesGroup) => {
        const idx = state.indexOf(field)
        idx > -1 && state.splice(idx, 1)
        if (valuesGroup && valuesGroup[field.name] != undefined) {
          valuesGroup[field.name] = undefined
        }
      })
    }

    const handleAddAll = evt => {
      evt.stopPropagation()
      handleChange(state => {
        allFields.forEach(field => {
          !state.includes(field) && state.push(field)
        })
      })
    }

    const handleRemoveAll = evt => {
      evt.stopPropagation()
      handleChange((state, values) => {
        if (values) {
          Object.keys(values).map(field => {
            if (values[field] != undefined) {
              values[field] = undefined
            }
          })
        }
        state.splice(0, state.length)
      })
    }

    const hasAddedFields = filteredFields.some(isAdded)
    const allFieldsAdded = filteredFields.length > 0 && filteredFields.every(isAdded)

    if (!filteredFields.length) {
      return null
    }

    return <>
      <DropdownMenuItem className="group hover:bg-transparent!" tag='span' onClick={evt => evt.stopPropagation()}>
        <div className="flex justify-between w-full">
          {children}
          <div className='gap-2 hidden group-hover:flex'>
            <small
              className={cn(
                'twy-text-accent hover:underline',
                !hasAddedFields && 'twy-text-base-neutral cursor-not-allowed no-underline hover:no-underline',
                hasAddedFields && 'cursor-pointer',
              )}
              onClick={evt => hasAddedFields && handleRemoveAll(evt)}
            >
              remove
            </small>
            <small
              className={cn(
                'twy-text-accent hover:underline',
                allFieldsAdded && 'twy-text-base-neutral cursor-not-allowed no-underline hover:no-underline',
                !allFieldsAdded && 'cursor-pointer',
              )}
              onClick={evt => !allFieldsAdded && handleAddAll(evt)}
            >
              add
            </small>
          </div>
        </div>
      </DropdownMenuItem>
      <this.DropdownFields data={filteredFields} onAdd={handleAdd} onRemove={handleRemove} isAdded={isAdded} />
      <Separator className='my-2' />
    </>
  }

  AddButton = () => {
    const fieldGroups = ['incommingTraffic', 'session', 'context', 'consumerData', 'customFields', 'metaFields']
    const hasMatches = fieldGroups.some(type => this.props.fields[type].some(field => this.matchesFieldSearch(field, this.state.fieldSearch)))
    return <DropdownMenu onOpenChange={open => !open && this.state.fieldSearch && this.setState({ fieldSearch: '' })}>
      <DropdownMenuTrigger asChild>
        <Button color='accent'>
          Add Field
          <ChevronDown size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='overflow-y-auto w-74 max-h-100' side='right'>
        <InputSearchList className='mb-2' placeholder="Fields" value={this.state.fieldSearch} onChange={fieldSearch => this.setState({ fieldSearch })} />
        {this.state.fieldSearch.trim() && !hasMatches ? <div className='py-2 pt-4 text-center twy-text-base-accent'>
          No fields found
        </div> : null}
        <this.DropdownFieldsGroup searchTerm={this.state.fieldSearch} type='incommingTraffic'>Incomming Traffic</this.DropdownFieldsGroup>
        <this.DropdownFieldsGroup searchTerm={this.state.fieldSearch} type='session'>Session</this.DropdownFieldsGroup>
        <this.DropdownFieldsGroup searchTerm={this.state.fieldSearch} type='context'>Context</this.DropdownFieldsGroup>
        <this.DropdownFieldsGroup searchTerm={this.state.fieldSearch} type='consumerData'>Consumer Data</this.DropdownFieldsGroup>
        <this.DropdownFieldsGroup searchTerm={this.state.fieldSearch} type='customFields'>Custom Fields</this.DropdownFieldsGroup>
        <this.DropdownFieldsGroup searchTerm={this.state.fieldSearch} type='metaFields'>Meta Fields</this.DropdownFieldsGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  }

  Form = () => {
    const { data, fields, children } = this.props

    useEffect(() => {
      fields && this.setState({ incommingTraffic: [...fields.incommingTraffic] })
    }, [fields])

    useEffect(() => {
      const state = {}

      if (data) {
        state.values = data
      }

      if (fields) {
        for (const group in data) {
          if (fields[group]) {
            state[group] = fields[group].filter(field => ![null, undefined, ''].includes(data[group][field.name]))
          }
        }
      }
      this.setState(state)
    }, [data, fields])

    return <Formsy onValidSubmit={this.handleValidSubmit}>
      <SheetBody className='mb-7 overflow-y-auto max-h-[70vh]'>
        <this.Group data={this.state.incommingTraffic}>Incomming Traffic</this.Group>
        <this.Group data={this.state.session}>Session</this.Group>
        <this.Group data={this.state.context}>Context</this.Group>
        <this.Group data={this.state.consumerData}>Consumer Data</this.Group>
        <this.Group data={this.state.customFields}>Custom Fields</this.Group>
        <this.Group data={this.state.metaFields}>Meta Fields</this.Group>
      </SheetBody>
      <SheetFooter className='flex justify-between absolute bottom-0 left-0 right-0 border-t twy-border-base'>
        <this.AddButton />
        <div className='flex gap-2'>
          {children}
          <Button type="submit" color="accent">Run</Button>
        </div>
      </SheetFooter>
    </Formsy>
  }

  render() {
    return <this.Form />
  }
}

ProfileToolForm.propTypes = propTypes
ProfileToolForm.defaultProps = {}
